import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { adminApi, getToken, clearToken } from "@/lib/auth";
import { Loader2, LogOut, Wallet, ScrollText, Mail, Users, Download, Film } from "lucide-react";
import { Footer } from "@/components/Footer";
import { PromoVideo } from "@/components/PromoVideo";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("sales"); // sales | club
  const [sales, setSales] = useState({ readings: [], count: 0, total_revenue: 0, currency: "eur" });
  const [club, setClub] = useState({ emails: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const logout = useCallback(() => {
    clearToken();
    navigate("/admin", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        adminApi.get("/admin/readings"),
        adminApi.get("/admin/emails"),
      ]);
      setSales(s.data);
      setClub(c.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expirée, reconnectez-vous.");
        logout();
      } else {
        toast.error("Chargement impossible.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const fmt = (iso) =>
    new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

  const exportCsv = () => {
    if (!club.emails.length) return;
    const rows = [["Email", "Date d'inscription"]].concat(
      club.emails.map((e) => [e.email, new Date(e.created_at).toISOString()])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `club-emails-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${club.emails.length} email(s) exporté(s).`);
  };

  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="oracle-bg" />
        <Loader2 className="relative z-10 text-[#C5A059] animate-spin" size={36} />
      </div>
    );
  }

  const tabs = [
    { id: "sales", label: "Guidances vendues", icon: ScrollText },
    { id: "club", label: "Club privé (emails)", icon: Users },
    { id: "promo", label: "Vidéo promo", icon: Film },
  ];

  return (
    <div className="relative min-h-screen flex flex-col">
      <div className="oracle-bg" />
      <div className="relative z-10 flex-1 max-w-4xl w-full mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="font-body text-[#A2A2A6] uppercase tracking-[0.3em] text-xs">Administration</span>
            <h1 className="font-display text-4xl tracking-tighter text-[#E5E5EA] mt-1">Tableau de bord</h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 text-[#A2A2A6] hover:text-[#E5E5EA] transition-colors text-sm font-body"
          >
            <LogOut size={16} strokeWidth={1.5} /> Déconnexion
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl p-6">
            <ScrollText className="text-[#C5A059] mb-3" strokeWidth={1.2} size={22} />
            <p data-testid="stat-count" className="font-display text-3xl text-[#E5E5EA]">{sales.count}</p>
            <p className="font-body text-[#A2A2A6] text-sm">Guidances vendues</p>
          </div>
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl p-6">
            <Wallet className="text-[#C5A059] mb-3" strokeWidth={1.2} size={22} />
            <p data-testid="stat-revenue" className="font-display text-3xl text-[#E5E5EA]">
              {Number(sales.total_revenue).toFixed(2)} €
            </p>
            <p className="font-body text-[#A2A2A6] text-sm">Chiffre d'affaires</p>
          </div>
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl p-6">
            <Mail className="text-[#C5A059] mb-3" strokeWidth={1.2} size={22} />
            <p data-testid="stat-emails" className="font-display text-3xl text-[#E5E5EA]">{club.count}</p>
            <p className="font-body text-[#A2A2A6] text-sm">Membres club privé</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 font-body text-sm transition-colors border-b-2 -mb-px ${
                  tab === t.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-[#A2A2A6] hover:text-[#E5E5EA]"
                }`}
              >
                <Icon size={16} strokeWidth={1.5} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "sales" && (
          <div data-testid="sales-list" className="space-y-4">
            {sales.readings.length === 0 ? (
              <div className="text-center py-16 border border-white/10 rounded-2xl bg-white/5">
                <Wallet className="text-[#C5A059]/60 mx-auto mb-3" strokeWidth={1} size={36} />
                <p className="font-display text-2xl text-[#E5E5EA]">Aucune vente pour l'instant</p>
              </div>
            ) : (
              sales.readings.map((r, i) => (
                <div
                  key={r.reading_id}
                  data-testid={`sale-item-${i}`}
                  className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl p-5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display text-lg text-[#C5A059]">Tirage</span>
                    <span className="font-body text-[#C5A059] text-sm">{Number(r.amount).toFixed(2)} €</span>
                  </div>
                  <p className="font-body text-[#A2A2A6] text-sm italic mt-1">
                    Contenu non conservé (confidentialité) : aucune donnée personnelle, question ou carte n'est enregistrée.
                  </p>
                  <p className="font-body text-[#A2A2A6]/60 text-xs mt-3">{fmt(r.created_at)}</p>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "club" && (
          <div data-testid="club-list" className="space-y-2">
            {club.emails.length > 0 && (
              <div className="flex items-center justify-between mb-4">
                <span className="font-body text-[#A2A2A6] text-sm">{club.count} membre(s)</span>
                <button
                  data-testid="export-csv-button"
                  onClick={exportCsv}
                  className="inline-flex items-center gap-2 font-body text-xs tracking-[0.1em] uppercase rounded-sm px-4 py-2 border transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#C5A059", color: "#1C1C1E", borderColor: "rgba(230,198,135,0.4)" }}
                >
                  <Download size={14} strokeWidth={1.6} />
                  Exporter en CSV
                </button>
              </div>
            )}
            {club.emails.length === 0 ? (
              <div className="text-center py-16 border border-white/10 rounded-2xl bg-white/5">
                <Users className="text-[#C5A059]/60 mx-auto mb-3" strokeWidth={1} size={36} />
                <p className="font-display text-2xl text-[#E5E5EA]">Aucun membre pour l'instant</p>
              </div>
            ) : (
              club.emails.map((e, i) => (
                <div
                  key={e.email}
                  data-testid={`club-item-${i}`}
                  className="flex items-center justify-between bg-white/5 backdrop-blur-2xl border border-white/10 rounded-lg px-4 py-3"
                >
                  <span className="font-body text-[#E5E5EA] text-sm">{e.email}</span>
                  <span className="font-body text-[#A2A2A6]/60 text-xs">{fmt(e.created_at)}</span>
                </div>
              ))
            )}
          </div>
        )}
        {tab === "promo" && (
          <div data-testid="promo-tab">
            <PromoVideo />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
